import { useState } from 'react';
import { Alert, Button, Field, Input, Select, formatDateTime, useConfirm } from '@busbook/ui';
import { Page } from '../components/Shell.js';
import { Column, DataTable, Drawer } from '../components/DataTable.js';
import { errorMessage, useResource } from '../lib/useResource.js';
import { api } from '../lib/api.js';
import { useSession } from '../lib/session.js';
import { useT } from '../i18n/index.js';

/** Staff management (docs/06 "/staff"). Gated on staff:manage; the last person who can
 *  manage staff is protected (DECISIONS D77). */

interface StaffMember {
  id: string;
  email: string;
  name: string;
  tenantRoleId: string | null;
  tenantRoleName: string | null;
  active: boolean;
  pending: boolean;
  lastLoginAt: string | null;
}

interface RoleOption {
  id: string;
  name: string;
  description: string | null;
}

export function Staff() {
  const t = useT();
  const { tenant, user } = useSession();
  const timezone = tenant?.timezone ?? 'UTC';
  const staff = useResource<StaffMember[]>('/admin/staff');
  const roles = useResource<RoleOption[]>('/admin/roles');
  const roleDescription = (roleId: string): string =>
    roles.data?.find((role) => role.id === roleId)?.description ?? '';
  const [inviting, setInviting] = useState(false);
  const [form, setForm] = useState({ email: '', name: '', tenantRoleId: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  /** The one and only sighting of a temporary password. Cleared when it is dismissed. */
  const [temporary, setTemporary] = useState<
    { member: StaffMember; temporaryPassword: string; expiresAt: string } | null
  >(null);
  const { ask, dialog } = useConfirm();

  const columns: Column<StaffMember>[] = [
    {
      key: 'name',
      header: t('f.name'),
      render: (member) => (
        <div>
          <p className="font-medium">
            {member.name}
            {member.id === user?.id && (
              <span style={{ color: 'var(--color-faint)' }}> · you</span>
            )}
          </p>
          <p className="text-xs" style={{ color: 'var(--color-faint)' }}>
            {member.email}
          </p>
        </div>
      ),
    },
    {
      key: 'role',
      header: t('f.role'),
      render: (member) => (
        <div>
          <p className="font-medium">{member.tenantRoleName ?? '—'}</p>
          {member.tenantRoleId && (
            <p className="text-xs" style={{ color: 'var(--color-muted)' }}>
              {roleDescription(member.tenantRoleId)}
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'lastLogin',
      header: t('sf.lastSignIn'),
      render: (member) =>
        member.lastLoginAt ? (
          <span className="tabular">{formatDateTime(member.lastLoginAt, timezone)}</span>
        ) : (
          <span style={{ color: 'var(--color-faint)' }}>never</span>
        ),
    },
    {
      key: 'status',
      header: t('f.status'),
      render: (member) => (
        <span
          style={{
            color: member.pending
              ? 'var(--signal-delayed)'
              : member.active
                ? 'var(--signal-departed)'
                : 'var(--color-faint)',
          }}
        >
          {member.pending ? 'Invitation sent' : member.active ? 'Active' : 'Deactivated'}
        </span>
      ),
    },
  ];

  async function resetPassword(member: StaffMember) {
    setError(null);
    setNotice(null);
    const confirmed = await ask({
      title: `Give ${member.name} a temporary password?`,
      body: 'This signs them out everywhere. They will need the new password to get back in.',
      confirmLabel: 'Reset password',
    });
    if (!confirmed) return;
    try {
      const result = await api.post<{ temporaryPassword: string; expiresAt: string }>(
        `/admin/staff/${member.id}/reset-password`,
        {},
      );
      // Held in component state, never refetched: only its hash is stored, so this is the one and
      // only time it can be read. Leaving the screen loses it, and the answer is to issue another.
      setTemporary({ member, ...result });
      await staff.reload();
    } catch (err) {
      setError(errorMessage(err, 'Could not reset this password.'));
    }
  }

  async function update(member: StaffMember, patch: { tenantRoleId?: string; active?: boolean }) {
    setError(null);
    setNotice(null);
    try {
      await api.put(`/admin/staff/${member.id}`, patch);
      await staff.reload();
    } catch (err) {
      setError(errorMessage(err, 'Could not update this staff member.'));
    }
  }

  async function invite() {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await api.post('/admin/staff', form);
      setNotice(`Invitation sent to ${form.email}. They set their own password from the link.`);
      setForm({ email: '', name: '', tenantRoleId: '' });
      setInviting(false);
      await staff.reload();
    } catch (err) {
      setError(errorMessage(err, 'Could not send that invitation.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Page
      title={t('nav.staff')}
      description={t('sf.subtitle')}
      actions={<Button onClick={() => setInviting(true)}>{t('sf.inviteSomeone')}</Button>}
    >
      {notice && (
        <div className="mb-4">
          <Alert tone="success">{notice}</Alert>
        </div>
      )}
      {temporary && (
        <div className="mb-4">
          <Alert tone="warning">
            <div className="flex flex-col gap-2">
              <span>
                Temporary password for <strong>{temporary.member.name}</strong>. It is shown once —
                copy it now, then close this. It expires {formatDateTime(temporary.expiresAt, timezone)} and can
                only be used to choose a new password.
              </span>
              <code className="select-all rounded px-2 py-1 text-sm" style={{ background: 'rgba(0,0,0,0.25)' }}>
                {temporary.temporaryPassword}
              </code>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => void navigator.clipboard?.writeText(temporary.temporaryPassword)}
                >
                  Copy
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setTemporary(null)}>
                  Done
                </Button>
              </div>
            </div>
          </Alert>
        </div>
      )}

      {error && (
        <div className="mb-4">
          <Alert tone="error">{error}</Alert>
        </div>
      )}

      <DataTable
        columns={columns}
        rows={staff.data}
        rowKey={(member) => member.id}
        loading={staff.loading}
        error={staff.error}
        empty={{ title: t('sf.noStaff'), description: t('sf.inviteHint') }}
      />

      {staff.data && staff.data.length > 0 && (
        <div className="mt-4 flex flex-col gap-2">
          {staff.data
            .filter((member) => member.id !== user?.id)
            .map((member) => (
              <div key={member.id} className="flex flex-wrap items-center gap-2 text-sm">
                <span className="w-52 truncate" style={{ color: 'var(--color-muted)' }}>
                  {member.name}
                </span>
                <Select
                  aria-label={`Role for ${member.name}`}
                  className="h-9 w-44"
                  value={member.tenantRoleId ?? ''}
                  onChange={(e) => update(member, { tenantRoleId: e.target.value })}
                >
                  {(roles.data ?? []).map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.name}
                    </option>
                  ))}
                </Select>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => update(member, { active: !member.active })}
                >
                  {member.active ? 'Deactivate' : 'Reactivate'}
                </Button>
                {member.active && !member.pending && (
                  <Button size="sm" variant="ghost" onClick={() => resetPassword(member)}>
                    Reset password
                  </Button>
                )}
              </div>
            ))}
        </div>
      )}

      <Drawer
        open={inviting}
        title={t('sf.inviteColleague')}
        onClose={() => setInviting(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setInviting(false)}>
              {t('f.cancel')}
            </Button>
            <Button onClick={invite} loading={busy} disabled={!form.email || !form.name || !form.tenantRoleId}>
              {t('sf.sendInvitation')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <p className="text-sm" style={{ color: 'var(--color-muted)' }}>
            They receive a link to set their own password. You can change their role at any time.
          </p>
          <Field label={t('f.name')} htmlFor="s-name">
            <Input id="s-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </Field>
          <Field label={t('f.email')} htmlFor="s-email">
            <Input
              id="s-email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </Field>
          <Field
            label={t('f.role')}
            htmlFor="s-role"
            hint={form.tenantRoleId ? roleDescription(form.tenantRoleId) : undefined}
          >
            <Select
              id="s-role"
              value={form.tenantRoleId}
              onChange={(e) => setForm({ ...form, tenantRoleId: e.target.value })}
            >
              <option value="" disabled>
                Choose a role
              </option>
              {(roles.data ?? []).map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>
      </Drawer>
      {dialog}
    </Page>
  );
}
